import { useState, useRef } from 'react'
import api from '../api/client'
import Modal from './Modal'
import Boton from './Boton'
import { fmtFecha, fmtHoraDe, fmtMonto } from '../utils/fecha'
import { colors, text, space, sx } from '../design/tokens'

/**
 * «🛒 Traer carrito» de la Nueva NV (Martin, 29 oct 2026: "terminarle el pedido
 * a un cliente lo quiero como traer carrito en la confección de una nota de
 * venta"). El vendedor busca el carrito por el código que le muestra la tienda
 * al cliente (o por email / link compartido) y trae sus renglones a la grilla,
 * con la plata del checkout. No crea nada: el carrito queda tomado cuando se
 * guarda la NV. La ficha nunca la pone el sistema: si falta, la sugiere.
 *
 * onTraer({ carrito, renglones, avisos, clienteSugerido })
 */
export default function TraerCarritoModal({ onClose, onTraer, sinCliente, listaNv }) {
  const [q, setQ] = useState('')
  const [resultados, setResultados] = useState(null)
  const [buscando, setBuscando] = useState(false)
  const [elegido, setElegido] = useState(null)
  const [detalle, setDetalle] = useState(null)
  const [error, setError] = useState(null)
  const [usarFicha, setUsarFicha] = useState(true)
  const timer = useRef(null)

  const buscar = (texto) => {
    setQ(texto); setElegido(null); setDetalle(null); setError(null)
    clearTimeout(timer.current)
    if (texto.trim().length < 3) { setResultados(null); return }
    timer.current = setTimeout(async () => {
      setBuscando(true)
      try {
        const { data } = await api.get('/carritos-web/buscar', { params: { q: texto.trim() } })
        const carritos = data.carritos || []
        setResultados(carritos)
        if (carritos.length === 1) elegir(carritos[0])
      } catch { setResultados([]) } finally { setBuscando(false) }
    }, 300)
  }

  const elegir = async (c) => {
    setElegido(c); setDetalle(null); setError(null)
    try {
      const { data } = await api.get(`/carritos-web/${c.id}/renglones`, { params: { tipo: c.tipo } })
      setDetalle(data)
    } catch (e) {
      setError(e.response?.data?.errors?.carrito?.[0] || e.response?.data?.message || 'No se pudo leer el carrito.')
    }
  }

  const traer = () => onTraer({
    carrito: elegido,
    renglones: detalle.renglones,
    avisos: detalle.avisos || [],
    clienteSugerido: sinCliente && usarFicha ? detalle.cliente_sugerido : null,
  })

  return (
    <Modal title="🛒 Traer el carrito de la tienda" onClose={onClose} maxWidth="760px">
      <label style={st.lbl}>Código del carrito, link compartido o email del cliente</label>
      <input autoFocus style={{ ...st.input, ...sx.mono, letterSpacing: '0.08em' }} value={q} onChange={e => buscar(e.target.value)} placeholder="Ej. T745H" />
      <div style={st.ayuda}>El cliente lo ve en su carrito de ligier.com.ar, abajo de «Finalizar compra» (5 letras).</div>

      {buscando && <div style={st.meta}>Buscando…</div>}
      {resultados && resultados.length === 0 && !buscando && (
        <div style={st.meta}>No hay ningún carrito con productos para «{q}». Pedile al cliente el código que le muestra el carrito.</div>
      )}
      {resultados && resultados.length > 1 && !elegido && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs, marginTop: space.md }}>
          {resultados.map(c => (
            <button key={`${c.tipo}-${c.id}`} type="button" onClick={() => elegir(c)} style={st.opcion}>
              <b style={sx.mono}>{c.codigo || '—'}</b>
              <span>{c.tipo === 'compartido' ? 'carrito compartido' : (c.nombre || c.email || 'anónimo')}</span>
              <span style={{ color: colors.textSec }}>{fmtFecha(c.visto_ultima_vez)} {fmtHoraDe(c.visto_ultima_vez)}</span>
              <span style={{ color: colors.textSec }}>{(c.lineas || []).reduce((a, l) => a + (l.cantidad || 0), 0)} u.</span>
            </button>
          ))}
        </div>
      )}

      {elegido && (
        <div style={st.caja}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', alignItems: 'center' }}>
            <b style={sx.mono}>{elegido.codigo}</b>
            <span>{elegido.tipo === 'compartido' ? 'carrito compartido' : (elegido.email || elegido.nombre || 'cliente anónimo')}</span>
            <span style={{ color: colors.textSec }}>visto {fmtFecha(elegido.visto_ultima_vez)} {fmtHoraDe(elegido.visto_ultima_vez)}</span>
            {resultados?.length > 1 && <span style={st.link} onClick={() => { setElegido(null); setDetalle(null); setError(null) }}>cambiar</span>}
          </div>
          {error && <div style={{ color: colors.err, marginTop: space.sm }}>{error}</div>}
          {!error && !detalle && <div style={st.meta}>Cotizando como la tienda…</div>}
          {detalle && (
            <>
              <table style={st.tabla}>
                <thead><tr>{['Código', 'Producto', 'Cant.', 'Precio', 'Desc.'].map(h => <th key={h} style={{ ...st.th, textAlign: h === 'Código' || h === 'Producto' ? 'left' : 'right' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {detalle.renglones.map((r, i) => (
                    <tr key={i}>
                      <td style={{ ...st.td, ...sx.mono, color: colors.textSec }}>{r.articulo?.codigo_alfa}</td>
                      <td style={st.td}>{r.articulo?.descripcion}{r.observacion && <div style={{ fontSize: text.sec, color: colors.textSec }}>{r.observacion}</div>}</td>
                      <td style={{ ...st.td, textAlign: 'right' }}>{r.cantidad}</td>
                      <td style={{ ...st.td, ...sx.mono, textAlign: 'right' }}>$ {fmtMonto(r.precio_lista)}</td>
                      <td style={{ ...st.td, ...sx.mono, textAlign: 'right' }}>{r.descuento ? `${r.descuento}%` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={st.totales}>
                <span>Subtotal $ {fmtMonto(detalle.subtotal)}</span>
                {detalle.descuento > 0 && <span>Promociones −$ {fmtMonto(detalle.descuento)}</span>}
                <b style={{ color: colors.text }}>Total $ {fmtMonto(detalle.total)}</b>
              </div>
              <div style={st.ayuda}>Mismos precios que el checkout de la tienda: entran a la NV con precio propio.</div>
              {detalle.avisos?.length > 0 && <div style={st.aviso}>⚠ Precio distinto al que veía el cliente: {detalle.avisos.join('; ')}.</div>}
              {detalle.tomado && <div style={st.aviso}>⚠ Este carrito ya lo tomó la {detalle.tomado.numero || `NV #${detalle.tomado.nota_venta_id}`}. Si lo traés igual, mirá que no quede la venta repetida.</div>}
              {listaNv && listaNv !== 'LISTA1' && <div style={st.aviso}>Tu lista es {listaNv}: los renglones del carrito entran con los precios de la tienda (LISTA1).</div>}
              {sinCliente && detalle.cliente_sugerido && (
                <label style={st.sugerido}>
                  <input type="checkbox" checked={usarFicha} onChange={e => setUsarFicha(e.target.checked)} />
                  <span>La NV todavía no tiene cliente: el email del carrito es de la ficha <b>{detalle.cliente_sugerido.codigo_presea || 's/n'} · {detalle.cliente_sugerido.nombre}</b>. Usar esa ficha.</span>
                </label>
              )}
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space.md, marginTop: space.xl }}>
        <Boton variante="ghost" type="button" onClick={onClose}>Cancelar</Boton>
        <Boton variante="primary" type="button" disabled={!detalle} onClick={traer}>
          {detalle ? `Traer ${detalle.renglones.length} renglones a la NV` : 'Traer a la NV'}
        </Boton>
      </div>
    </Modal>
  )
}

const st = {
  lbl: { display: 'block', fontSize: text.sec, fontWeight: 600, color: colors.textSub, marginBottom: space.xs },
  input: { ...sx.input, width: '100%', boxSizing: 'border-box', fontSize: text.title },
  ayuda: { fontSize: text.sec, color: colors.textSec, marginTop: space.xs },
  meta: { fontSize: text.sec, color: colors.textMeta, marginTop: space.md },
  opcion: { display: 'flex', gap: space.lg, alignItems: 'center', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, background: colors.bg, cursor: 'pointer', textAlign: 'left', fontSize: text.body },
  caja: { marginTop: space.lg, border: `1px solid ${colors.border}`, borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: space.sm },
  link: { color: colors.link, cursor: 'pointer', textDecoration: 'underline', fontSize: text.sec },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { ...sx.th, padding: '4px 8px' },
  td: { padding: '6px 8px', borderTop: `1px solid ${colors.borderSoft}`, verticalAlign: 'top' },
  totales: { display: 'flex', justifyContent: 'flex-end', gap: space.xl, fontSize: text.body, color: colors.textSec },
  aviso: { background: colors.warnBg, color: colors.warn, borderRadius: 6, padding: '8px 12px', fontSize: text.sec },
  sugerido: { display: 'flex', gap: space.md, alignItems: 'flex-start', background: colors.linkBg, color: colors.text, borderRadius: 6, padding: '8px 12px', fontSize: text.sec, cursor: 'pointer' },
}
